// Registration form component for multi-tenant platform
// Requirements: 2.1, 2.2, 2.4, 2.6

import React, { useState, useEffect } from 'react';
import { AlertTriangleIcon } from '../common/icons';
import { RegisterCredentials, FormValidation, PasswordValidation } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';
import { CognitoAuthService } from '../../services/cognitoAuthService';
import './RegisterForm.css';

interface RegisterFormProps {
  onRegistrationSuccess: (message: string) => void;
  onSwitchToLogin: () => void;
  loading?: boolean;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onRegistrationSuccess,
  onSwitchToLogin,
  loading = false
}) => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    email: '',
    password: '',
    confirmPassword: '',
    tenantName: ''
  });

  const [validation, setValidation] = useState<FormValidation>({
    email: { isValid: true, error: '' },
    password: { isValid: true, error: '', strength: 'weak' },
    confirmPassword: { isValid: true, error: '' },
    tenantName: { isValid: true, error: '' }
  });

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    isValid: false,
    errors: [],
    strength: 'weak'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Real-time email validation
  useEffect(() => {
    if (credentials.email) {
      const emailValidation = CognitoAuthService.validateEmail(credentials.email);
      setValidation(prev => ({
        ...prev,
        email: emailValidation
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        email: { isValid: true, error: '' }
      }));
    }
  }, [credentials.email]);

  // Real-time password validation
  useEffect(() => {
    if (credentials.password) {
      const passwordVal = CognitoAuthService.validatePassword(credentials.password);
      setPasswordValidation(passwordVal);
      
      setValidation(prev => ({
        ...prev,
        password: {
          isValid: passwordVal.isValid,
          error: passwordVal.isValid ? '' : passwordVal.errors[0],
          strength: passwordVal.strength
        }
      }));
    } else {
      setPasswordValidation({ isValid: false, errors: [], strength: 'weak' });
      setValidation(prev => ({
        ...prev,
        password: { isValid: true, error: '', strength: 'weak' }
      }));
    }
  }, [credentials.password]);

  // Real-time confirm password validation
  useEffect(() => {
    if (credentials.confirmPassword) {
      const isValid = credentials.password === credentials.confirmPassword;
      setValidation(prev => ({
        ...prev,
        confirmPassword: {
          isValid,
          error: isValid ? '' : 'Passwords do not match'
        }
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        confirmPassword: { isValid: true, error: '' }
      }));
    }
  }, [credentials.password, credentials.confirmPassword]);

  // Real-time tenant name validation
  useEffect(() => {
    if (credentials.tenantName) {
      const isValid = credentials.tenantName.trim().length >= 2;
      setValidation(prev => ({
        ...prev,
        tenantName: {
          isValid,
          error: isValid ? '' : 'Organization name must be at least 2 characters'
        }
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        tenantName: { isValid: true, error: '' }
      }));
    }
  }, [credentials.tenantName]);

  const handleInputChange = (field: keyof RegisterCredentials, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear general error
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    const emailValidation = CognitoAuthService.validateEmail(credentials.email);
    const passwordVal = CognitoAuthService.validatePassword(credentials.password);
    const confirmPasswordValid = credentials.password === credentials.confirmPassword;
    const tenantNameValid = credentials.tenantName ? credentials.tenantName.trim().length >= 2 : false;

    const newValidation: FormValidation = {
      email: emailValidation,
      password: {
        isValid: passwordVal.isValid,
        error: passwordVal.isValid ? '' : passwordVal.errors[0],
        strength: passwordVal.strength
      },
      confirmPassword: {
        isValid: confirmPasswordValid,
        error: confirmPasswordValid ? '' : 'Passwords do not match'
      },
      tenantName: {
        isValid: tenantNameValid,
        error: tenantNameValid ? '' : 'Organization name must be at least 2 characters'
      }
    };

    setValidation(newValidation);

    return emailValidation.isValid && 
           passwordVal.isValid && 
           confirmPasswordValid && 
           tenantNameValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || loading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // First register the user
      const result = await CognitoAuthService.register(credentials);
      
      // For Cognito, we need to handle email verification first
      // Don't automatically log in - user needs to verify email first
      onRegistrationSuccess(`${result.message} Please check your email and verify your account before signing in.`);
    } catch (err: any) {
      console.error('Registration error:', err);
      
      // Handle specific error messages
      if (err.message?.includes('already exists')) {
        setError('An account with this email already exists. Please use a different email or sign in.');
      } else if (err.message?.includes('Network Error')) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = validation.email.isValid && 
                     validation.password?.isValid && 
                     validation.confirmPassword?.isValid &&
                     validation.tenantName?.isValid &&
                     credentials.email && 
                     credentials.password &&
                     credentials.confirmPassword &&
                     credentials.tenantName;

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong': return '#10b981';
      case 'medium': return '#f59e0b';
      default: return '#ef4444';
    }
  };

  return (
    <div className="register-form">
      <div className="register-form__header">
        <h2>Create Account</h2>
        <p>Join us today! Create your account to get started.</p>
      </div>

      <form onSubmit={handleSubmit} className="register-form__form">
        {error && (
          <div className="register-form__error" role="alert">
            <AlertTriangleIcon width={16} height={16} className="register-form__error-icon" />
            {error}
          </div>
        )}

        <div className="register-form__field">
          <label htmlFor="email" className="register-form__label">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={credentials.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`register-form__input ${
              !validation.email.isValid ? 'register-form__input--error' : ''
            }`}
            placeholder="Enter your email address"
            disabled={isSubmitting || loading}
            autoComplete="email"
            required
          />
          {!validation.email.isValid && (
            <span className="register-form__field-error">
              {validation.email.error}
            </span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="tenantName" className="register-form__label">
            Organization Name
          </label>
          <input
            id="tenantName"
            type="text"
            value={credentials.tenantName}
            onChange={(e) => handleInputChange('tenantName', e.target.value)}
            className={`register-form__input ${
              !validation.tenantName?.isValid ? 'register-form__input--error' : ''
            }`}
            placeholder="Enter your organization name"
            disabled={isSubmitting || loading}
            autoComplete="organization"
            required
          />
          {!validation.tenantName?.isValid && (
            <span className="register-form__field-error">
              {validation.tenantName?.error}
            </span>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="password" className="register-form__label">
            Password
          </label>
          <div className="register-form__password-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`register-form__input ${
                !validation.password?.isValid ? 'register-form__input--error' : ''
              }`}
              placeholder="Create a strong password"
              disabled={isSubmitting || loading}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="register-form__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isSubmitting || loading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          
          {credentials.password && (
            <div className="register-form__password-strength">
              <div className="register-form__strength-bar">
                <div 
                  className="register-form__strength-fill"
                  style={{
                    width: passwordValidation.strength === 'strong' ? '100%' : 
                           passwordValidation.strength === 'medium' ? '66%' : '33%',
                    backgroundColor: getStrengthColor(passwordValidation.strength)
                  }}
                />
              </div>
              <span 
                className="register-form__strength-text"
                style={{ color: getStrengthColor(passwordValidation.strength) }}
              >
                {passwordValidation.strength.charAt(0).toUpperCase() + passwordValidation.strength.slice(1)} Password
              </span>
            </div>
          )}
          
          {!validation.password?.isValid && passwordValidation.errors.length > 0 && (
            <div className="register-form__password-requirements">
              <p className="register-form__requirements-title">Password must contain:</p>
              <ul className="register-form__requirements-list">
                {passwordValidation.errors.map((error, index) => (
                  <li key={index} className="register-form__requirement">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="register-form__field">
          <label htmlFor="confirmPassword" className="register-form__label">
            Confirm Password
          </label>
          <div className="register-form__password-wrapper">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={credentials.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`register-form__input ${
                !validation.confirmPassword?.isValid ? 'register-form__input--error' : ''
              }`}
              placeholder="Confirm your password"
              disabled={isSubmitting || loading}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="register-form__password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={isSubmitting || loading}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {!validation.confirmPassword?.isValid && (
            <span className="register-form__field-error">
              {validation.confirmPassword?.error}
            </span>
          )}
        </div>

        <button
          type="submit"
          className={`register-form__submit ${
            !isFormValid || isSubmitting || loading ? 'register-form__submit--disabled' : ''
          }`}
          disabled={!isFormValid || isSubmitting || loading}
        >
          {isSubmitting ? (
            <>
              <span className="register-form__spinner"></span>
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </button>

        <div className="register-form__footer">
          <p>
            Already have an account?{' '}
            <button
              type="button"
              className="register-form__link"
              onClick={onSwitchToLogin}
              disabled={isSubmitting || loading}
            >
              Sign in here
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;